import { getRouteApi } from '@tanstack/react-router'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import CancelIcon from '@/assets/icons/cancel.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import UnionIcon from '@/assets/icons/union.svg?react'
import LinkText from '@/components/LinkText/LinkText'
import Table, {
  type Column,
  type TableProps,
  type TableAction
} from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import Tag from '@/components/Tag/Tag'
import type useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { formatDate } from '@/utils/format-date'
import { getColumnWidth } from '@/utils/get-column-width'

import type { CurriculumAim } from '../constants/curriculum-aim'
import { curriculumStatusToTagColorMap } from '../constants/curriculum-status-to-tag-color-map'
import type {
  CurriculumList,
  CurriculumListItem,
  CurriculumStatus
} from '../queries/useCurriculums'

type CurriculumsManagementListTableProps = {
  onDuplicateClick: (row: CurriculumListItem) => void
  onRemoveClick: (row: CurriculumListItem) => void
  onBulkRemoveClick: (curriculumIds: string[]) => void
  rowSelection: ReturnType<typeof useRowSelection>
  data?: CurriculumList
} & Pick<
  TableProps<CurriculumListItem>,
  | 'count'
  | 'totalCount'
  | 'pagination'
  | 'isLoading'
  | 'onChangePage'
  | 'onChangePageSize'
  | 'sorting'
  | 'onChangeSorting'
>

const routeApi = getRouteApi('/_auth/curricula/')

const CurriculumsManagementListTable = (
  props: CurriculumsManagementListTableProps
) => {
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation(['curriculumsManagement', 'common'])

  const { isExtraLarge } = useScreenResolution()

  const columns: Column<CurriculumListItem>[] = [
    {
      dataIndex: 'name',
      key: 'name',
      sorterKey: 'name',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.name'),
      render: (_, { id, name }) => (
        <LinkText
          to="/curricula/$curriculumId"
          params={{
            curriculumId: id
          }}
        >
          {name}
        </LinkText>
      )
    },
    {
      dataIndex: ['educationLevel', 'name'],
      key: 'educationLevel',
      sorterKey: 'education_level',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.education-level')
    },
    {
      dataIndex: 'aim',
      key: 'aim',
      sorterKey: 'aim',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.aim'),
      render: (value: CurriculumAim) => t(`aim.${value}`)
    },
    {
      dataIndex: 'status',
      key: 'status',
      sorterKey: 'status',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('label.status'),
      render: (value: CurriculumStatus) => (
        <Tag label={value} color={curriculumStatusToTagColorMap[value]} />
      )
    },
    {
      dataIndex: 'created',
      key: 'created',
      sorterKey: 'created',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
      title: t('label.created'),
      render: (value: Date) => formatDate(value)
    }
  ]

  const actions: TableAction<CurriculumListItem>[] = [
    {
      icon: <EditIcon />,
      text: t('button.edit'),
      onClick: row =>
        navigate({
          to: '/curricula/$curriculumId/edit',
          params: { curriculumId: row.id }
        }),
      isHidden: row => !row.canBeEdited
    },
    {
      icon: <UnionIcon />,
      onClick: props.onDuplicateClick,
      text: t('button.duplicate')
    },
    {
      icon: <CancelIcon />,
      onClick: props.onRemoveClick,
      text: t('button.remove'),
      variantAction: 'danger'
    }
  ]

  const bulkActions = [
    {
      text: t('button.remove'),
      icon: <DeleteIcon />,
      onClick: () => props.onBulkRemoveClick(props.rowSelection.selectedItems)
    }
  ]

  return (
    <Table
      id="curriculums-management-table"
      data={props.data ?? []}
      columns={columns}
      count={props.count}
      totalCount={props.totalCount}
      actions={actions}
      rightTopBar={
        <TableTopBarActions
          selectedItemsLength={props.rowSelection.selectedItems.length}
          actions={bulkActions}
        />
      }
      rowSelection={{
        selectedItems: props.rowSelection.selectedItems,
        allCheckboxValue: props.rowSelection.getSelectAllCheckboxValue(),
        count: props.count ?? 0,
        labelHeader: selected =>
          t(
            selected
              ? 'label.deselect-all-curricula'
              : 'label.select-all-curricula'
          ),
        labelItem: ({ selected, name }) =>
          t(
            selected ? 'label.deselect-curriculum' : 'label.select-curriculum',
            {
              CURRICULUM: name
            }
          ),
        onSelect: props.rowSelection.onSelect,
        onSelectAll: props.rowSelection.onSelectAll
      }}
      pagination={props.pagination}
      isLoading={props.isLoading}
      onChangePage={props.onChangePage}
      onChangePageSize={props.onChangePageSize}
      sorting={props.sorting}
      onChangeSorting={props.onChangeSorting}
      caption={t('header.curricula-management')}
    />
  )
}

export default memo(CurriculumsManagementListTable)
